import {Asset, AssetType, XMLData} from "../../redux/assets-slice";
import { Bookmark } from "../../redux/bookmarks-slice";
import {t} from "../../localization";
import {backendHost} from "./misc";
import LocalScene from "../../viewer/js/projections";
import { fse, getTemporaryFile } from "../../electron-modules";
import { PythonExecutable } from "../../executable";
import { getLocalizedProjectName, getMatrixValues, pathWithExtension } from "../../utilities";
import { SettingsState } from "../../redux/settings-slice";

const SHP_FILE_TYPES = ["shp", "dbf"];
const POTREE_FILES = ['hierarchy.bin', 'octree.bin', 'metadata.json'];

interface ExporterOpts {
  name: string;
  assets: Asset[];
  state: any;
  token: string;
  onError?(title: string, message: string): void;
  onUpdate?(message: string): void;
  onProgress?(percent: number): void;
  onFinish?(projectID: string): void;
}

const exporterOptsDefaults = {
  onError: () => {},
  onUpdate: () => {},
  onProgress: () => {},
  onFinish: () => {}
};

const getAssetLayers = (asset) => {
  return asset.data.map(shpLayer => ({
    name: shpLayer.name,
    originalId: shpLayer.id,
    visible: shpLayer.visible,
    color: shpLayer.color
  }));
};

const getBaseAsset = (asset) => {
  return {
    name: asset.name,
    visible: asset.visible,
    originalId: asset.id,
  };
};

const getTagsData = (asset) => {
  return asset.data.items.map(tag => ({
    originalId: tag.id,
    name: tag.name,
    x: tag.x,
    y: tag.y,
    z: tag.z,
    comment: tag.comment
  }));
};

const getImageData = (asset) => {
  return asset.data.map(image => {
    const values = {
      originalId: image.id,
      filename: image.name,
      x: image.x,
      y: image.y,
      z: image.z,
      roll: image.roll,
      pitch: image.pitch,
      yaw: image.yaw
    };

    /** Planar images have additional information */
    if (asset.type === AssetType.Planar) {
      return {
        ...values,
        config: image.config
      };
    }

    return values;
  });
};

const getSettings = (settings: SettingsState) => {
  return {
    colorMap: settings.colorMap,
    colorType: settings.colorType,
    dynamicSize: settings.dynamicSize,
    circularPoints: settings.circularPoints,
    arrowMarkers: settings.arrowMarkers,
    activeTab: settings.activeTab,
    opacity: settings.opacity,
    cloudMaxPoints: settings.cloudMaxPoints,
    cloudDistance: settings.cloudDistance,
    cloudScale: settings.cloudScale,
    cloudMinimum: settings.cloudMinimum,
    switched: settings.switched,
    drawerOpen: settings.drawerOpen,
    heightClip: {...settings.heightClip},
    intensityClip: {...settings.intensityClip},
    overrideSettings: settings.overrideSettings,
    classifications: {
      available: settings.classifications.map(classification => classification.available),
      visible: settings.classifications.map(classification => classification.visible)
    }
  };
};

const getAerialState = (aerialState) => {
  return {
    center: aerialState.center,
    zoom: aerialState.zoom,
    rotation: aerialState.rotation,
    basemap: aerialState.basemap
  };
};

const getSceneState = (sceneState) => {
  return {
    camera: sceneState.camera,
    fov: sceneState.fov,
    lookat: sceneState.lookat,
    angles: sceneState.angles,
    orbit: sceneState.orbit,
    pivot: sceneState.pivot
  };
};

const createProject = async (state, token): Promise<any> => {
  const folders = state.folders.map(folder => {
    const assets = state.assets
      .filter(asset => asset.folderID === folder.id) as Asset[];

    const tags = assets
      .filter(asset => asset.type === AssetType.Tag)
      .map(tagAsset => ({
        ...getBaseAsset(tagAsset),
        tags: getTagsData(tagAsset)
      }));

    const panoramics = assets
      .filter(asset => asset.type === AssetType.Panoramic)
      .map(panoAsset => ({
        ...getBaseAsset(panoAsset),
        images: getImageData(panoAsset)
      }));

    const planars = assets
      .filter(asset => asset.type === AssetType.Planar)
      .map(panoAsset => ({
        ...getBaseAsset(panoAsset),
        images: getImageData(panoAsset)
      }));

    const pointClouds = assets
      .filter(asset => {
        return (asset.type === AssetType.LAS)
          || (asset.type === AssetType.E57Points)
          || (asset.type === AssetType.Potree);
      })
      .map(pointCloudAsset => ({
        ...getBaseAsset(pointCloudAsset)
      }));

    const orthomosaics = assets
      .filter(asset => asset.type === AssetType.OrthoMosaic)
      .map(orthoAsset => ({
        ...getBaseAsset(orthoAsset),
        imageData: orthoAsset.data
      }));

    const landxmls = assets
      .filter(asset => asset.type === AssetType.LandXML)
      .map(landXMLAsset => ({
        ...getBaseAsset(landXMLAsset),
        ...(landXMLAsset.data as XMLData)
      }));

    const ifcFiles = assets
      .filter(asset => asset.type === AssetType.IFC)
      .map(ifcAsset => ({
        ...getBaseAsset(ifcAsset)
      }));

    const shpFiles = assets
      .filter(asset => asset.type === AssetType.SHP)
      .map(shpAsset => ({
        ...getBaseAsset(shpAsset),
        layers: getAssetLayers(shpAsset)
      }));

    const dxfFiles = assets
      .filter(asset => asset.type === AssetType.DXF)
      .map(dxfAsset => ({
        ...getBaseAsset(dxfAsset),
        layers: getAssetLayers(dxfAsset)
      }));

    return {
      name: folder.name,
      originalId: folder.id,
      default: folder.default,
      tags,
      panoramics,
      planars,
      pointClouds,
      orthomosaics,
      landxmls,
      ifcFiles,
      shpFiles,
      dxfFiles
    };
  });

  const projections = {
    data: {
      type: state.projections.data.type,
      name: state.projections.data.name,
      default: state.projections.data.default,
      string: state.projections.data.string,
      transform: state.projections.data.transform
    },
    view: {
      type: state.projections.view.type,
      name: state.projections.view.name,
      default: state.projections.view.default,
      string: state.projections.view.string,
      transform: state.projections.view.transform
    }
  };

  const settings = getSettings(state.settings);

  const bookmarks = state.bookmarks.map((bookmark: Bookmark) => {
    return {
      originalId: bookmark.id,
      name: bookmark.name,
      position: bookmark.position,
      visible: bookmark.visible,
      visibleAssets: bookmark.visibleAssets,
      settings: getSettings(bookmark.settings),
      aerialState: getAerialState(bookmark.aerialState),
      sceneState: getSceneState(bookmark.sceneState),
    };
  });

  const {success, rotation, offset}
    = await getMatrixValues(state.camera.transform);

  const transform = success ? {
    rotation: rotation.toArray(),
    translation: offset.toArray()
  } : null;

  const adjustments = Object.keys(state.camera.adjustments).map(camera => {
    const rotation = state.camera.adjustments[camera];
    return {camera, rotation};
  });

  const cameraState = {
    transform: transform,
    adjustments: adjustments,
    height: state.camera.height,
    aerialState: getAerialState(state.camera.aerialState),
    sceneState: getSceneState(state.camera.sceneState)
  };

  const localizedProjectName = getLocalizedProjectName(state.project.name);

  const request = await fetch(`${backendHost}/api/projects`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      name: localizedProjectName,
      configVersion: state.project.configVersion,
      folders,
      bookmarks,
      measurementUnits: state.projections.measureUnits,
      projections,
      settings,
      cameraState
    })
  });

  const response = await request.json();
  if (!response.id) {
    throw new Error("Error creating new project");
  }

  return response;
};

const getProjectAssets = (project, opts) => {
  const panoramics = project.panoramics.map(panoramic => {
    const panoramicAsset = opts.state.assets
      .find(asset => asset.id === panoramic.originalId);

    return panoramicAsset.data.map(image => {
      return {
        url: getUploadURL(project, panoramic),
        path: image.path,
        name: image.name
      };
    });
  }).flat();

  const planars = project.planars.map(planar => {
    const planarAsset = opts.state.assets
      .find(asset => asset.id === planar.originalId);

    return planarAsset.data.map(image => {
      return {
        url: getUploadURL(project, planar),
        path: image.path,
        name: image.name
      };
    });
  }).flat();

  const pointClouds = project.pointClouds.map(pointCloud => {
    const pointCloudAsset = opts.state.assets
      .find(asset => asset.id === pointCloud.originalId);

    if (pointCloudAsset.type === AssetType.Potree) {
      return POTREE_FILES.map(filePath => {
        return {
          url: getUploadURL(project, pointCloud),
          path: `${pointCloudAsset.path}/${filePath}`,
          name: pointCloudAsset.name,
        };
      });
    }

    return [{
      url: getUploadURL(project, pointCloud),
      path: pointCloudAsset.path,
      name: pointCloudAsset.name,
      processPoints: true,
      units: LocalScene.dataProjectionUnits
    }];
  }).flat();

  const ifcFiles = project.ifcFiles.map(ifcFile => {
    const ifcAsset = opts.state.assets
      .find(asset => asset.id === ifcFile.originalId);

    return {
      url: getUploadURL(project, ifcFile),
      path: ifcAsset.path,
      name: ifcAsset.name
    };
  });

  const shpFiles = project.shpFiles.map(shpFile => {
    const shpAsset = opts.state.assets
      .find(asset => asset.id === shpFile.originalId);

    return SHP_FILE_TYPES.map(fileType => {
      return {
        url: getUploadURL(project, shpFile),
        path: pathWithExtension(shpAsset.path, fileType),
        name: shpFile.name,
      };
    });
  }).flat();

  const dxfFiles = project.dxfFiles.map(dxfFile => {
    const ifcAsset = opts.state.assets
      .find(asset => asset.id === dxfFile.originalId);

    return {
      url: getUploadURL(project, dxfFile),
      path: ifcAsset.path,
      name: ifcAsset.name
    };
  });

  return [
    ...panoramics,
    ...planars,
    ...pointClouds,
    ...ifcFiles,
    ...shpFiles,
    ...dxfFiles
  ];
};

const getUploadURL = (project, asset) => {
  return `${backendHost}/api/projects/${project.id}/assets/${asset.id}/files`;
};

export const getCloudExporter = (): [(opts: ExporterOpts) => Promise<void>, () => void] => {
  const exe = new PythonExecutable();

  const startExport = async (opts: ExporterOpts) => {
    opts = {...exporterOptsDefaults, ...opts};

    let project;

    try {
      project = await createProject(opts.state, opts.token);
    } catch {
      // Error creating project (missing required values / server error)
      opts.onError(t("dialog.general-error-title"), t("dialog.export-error-cloud"));
      return;
    }

    const jsonData = {
      assets: getProjectAssets(project, opts),
      token: opts.token
    };

    // Save to temporary json file
    const tempOutputPath = getTemporaryFile("json");
    const text = JSON.stringify(jsonData, null, 2);
    await fse.writeFile(tempOutputPath, text, 'utf8');

    let commands = [
      "-p", "upload_engine_assets",
      "--input_json", tempOutputPath
    ];

    exe.run({
      command: commands,
      onLine: jsonData => {
        if (jsonData.progress) {
          opts.onProgress(jsonData.progress);
        }

        if (jsonData.update) {
          opts.onUpdate(jsonData.update);
        }
      },
      onClose: (error) => {
        if (error) return;
        opts.onFinish(project.id);
      }
    });
  };

  const cancelExport = () => {
    exe.destroy();
  };

  return [startExport, cancelExport];
};